<template>
  <v-autocomplete
    ref="regionAutocomplete"
    :value="value"
    :label="label"
    :items="regionItems"
    item-text="text"
    item-value="value"
    no-filter
    :search-input.sync="search"
    :menu-props="{ maxHeight: 320 }"
    :rules="regionRules"
    v-bind="$attrs"
    @input="onInput"
  >
    <template #label>
      <slot name="label">
        {{ label }}
      </slot>
    </template>

    <template #selection="{ item }">
      <span>{{ item.selectionText || item.text }}</span>
    </template>

    <template #item="{ item, attrs, on }">
      <v-subheader
        v-if="item.type === 'region-search-header'"
        :id="item.headerId"
        class="region-search-header"
        role="presentation"
      >
        {{ item.text }}
      </v-subheader>
      <v-list-item
        v-else
        :class="{
          'region-group-option': item.type === 'region' && item.hasBranches,
          'region-group-option--expanded': item.type === 'region' && item.hasBranches && currentExpandedRegion === item.region,
          'region-office-item': item.type === 'office',
        }"
        v-bind="getRegionItemAttrs(item, attrs)"
        v-on="getRegionItemEvents(item, on)"
      >
        <v-list-item-content :class="{ 'region-office-option': item.type === 'office' }">
          <v-list-item-title>{{ item.text }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.type === 'region' && item.hasBranches && currentExpandedRegion === item.region">
            Выберите город
          </v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="item.type === 'region' && item.hasBranches">
          <v-icon small>{{ currentExpandedRegion === item.region ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-list-item-action>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
export default {
  name: 'RegistrationRegionSelect',
  inheritAttrs: false,
  props: {
    value: {
      type: String,
      default: '',
    },
    regions: {
      type: Array,
      default: () => [],
    },
    regionBranches: {
      type: Object,
      default: () => ({}),
    },
    label: {
      type: String,
      default: 'Регион',
    },
  },
  data() {
    return {
      expandedRegion: '',
      collapsedRegion: '',
      search: '',
    }
  },
  computed: {
    regionRules() {
      return [
        v => !!v || 'Требуется выбрать регион',
        v => this.isKnownRegionValue(v) || 'Выберите регион из списка',
        v => !this.hasBranches(v) || 'Выберите город из списка',
      ]
    },
    allRegionItems() {
      const items = []
      this.regions.forEach(region => {
        items.push(this.createRegionItem(region))
        this.branches(region).forEach(branch => {
          items.push(this.createOfficeItem(region, branch))
        })
      })
      return items
    },
    allRegionItemsByValue() {
      return this.allRegionItems.reduce((acc, item) => {
        acc[item.value] = item
        return acc
      }, {})
    },
    selectedText() {
      const selectedItem = this.allRegionItemsByValue[this.value]
      return selectedItem ? selectedItem.selectionText || selectedItem.text : ''
    },
    searchQuery() {
      const search = this.normalize(this.search)
      return search === this.normalize(this.selectedText) ? '' : search
    },
    selectedItem() {
      return this.allRegionItemsByValue[this.value] || null
    },
    currentExpandedRegion() {
      if (this.expandedRegion) {
        return this.expandedRegion
      }

      if (
        !this.searchQuery &&
        this.selectedItem &&
        this.selectedItem.type === 'office' &&
        this.selectedItem.region !== this.collapsedRegion
      ) {
        return this.selectedItem.region
      }

      return ''
    },
    regionItems() {
      if (this.searchQuery) {
        return this.searchRegionItems()
      }

      const items = []
      this.regions.forEach(region => {
        items.push(this.createRegionItem(region))
        if (this.currentExpandedRegion === region) {
          this.branches(region).forEach(branch => {
            items.push(this.createOfficeItem(region, branch))
          })
        }
      })
      return items
    },
  },
  methods: {
    createRegionItem(region) {
      return {
        type: 'region',
        value: region,
        text: region,
        selectionText: region,
        region,
        hasBranches: this.hasBranches(region),
      }
    },
    createOfficeItem(region, branch) {
      return {
        type: 'office',
        value: branch.value,
        text: branch.label,
        selectionText: branch.selectionLabel || branch.label,
        region,
        searchRegionHeaderId: '',
      }
    },
    createRegionSearchHeaderItem(region) {
      return {
        type: 'region-search-header',
        value: `search-header:${region}`,
        text: region,
        region,
        headerId: `region-search-header-${this.normalize(region).replace(/[^a-z0-9]+/g, '-')}`,
      }
    },
    branches(region) {
      const branches = this.regionBranches[region]
      if (Array.isArray(branches)) {
        return branches
      }
      return branches ? Object.values(branches) : []
    },
    hasBranches(region) {
      return Boolean(region && this.branches(region).length)
    },
    isKnownRegionValue(value) {
      if (!value) {
        return true
      }
      return Boolean(this.allRegionItemsByValue[value])
    },
    searchRegionItems() {
      const items = []
      this.regions.forEach(region => {
        const regionItem = this.createRegionItem(region)
        const regionSearchHeaderItem = this.createRegionSearchHeaderItem(region)
        const branchItems = this.branches(region).map(branch => this.createOfficeItem(region, branch))
        const regionMatches = this.itemMatchesSearch(regionItem)
        const matchedBranches = branchItems.filter(item => this.itemMatchesSearch(item, { officeOnly: true }))

        if (regionMatches || matchedBranches.length) {
          if (matchedBranches.length) {
            items.push(regionSearchHeaderItem)
            items.push(...matchedBranches.map(item => ({
              ...item,
              searchRegionHeaderId: regionSearchHeaderItem.headerId,
            })))
          } else {
            items.push(regionItem)
          }
        }
      })
      return items
    },
    itemMatchesSearch(item, { officeOnly = false } = {}) {
      if (officeOnly) {
        return [item.text, item.selectionText, item.value]
          .filter(Boolean)
          .some(value => this.normalize(value).includes(this.searchQuery))
      }

      const values = [
        item.text,
        item.selectionText,
        item.value,
        item.region,
      ]

      return values.filter(Boolean).some(value => this.normalize(value).includes(this.searchQuery))
    },
    normalize(value) {
      return String(value || '').trim().toLowerCase().replace(/ё/g, 'е')
    },
    getRegionItemAttrs(item, attrs) {
      if (item.type === 'office' && item.searchRegionHeaderId) {
        return {
          ...attrs,
          'aria-label': item.selectionText || `${item.region}, ${item.text}`,
          'aria-describedby': item.searchRegionHeaderId,
        }
      }

      if (!(item.type === 'region' && item.hasBranches)) {
        return attrs
      }

      const buttonAttrs = { ...attrs }
      delete buttonAttrs.role
      delete buttonAttrs['aria-selected']

      return {
        ...buttonAttrs,
        role: 'button',
        tabindex: buttonAttrs.tabindex || '0',
        'aria-expanded': this.currentExpandedRegion === item.region ? 'true' : 'false',
        'aria-haspopup': 'listbox',
      }
    },
    getRegionItemEvents(item, on) {
      if (!(item.type === 'region' && item.hasBranches)) {
        return on
      }

      return {
        ...on,
        mousedown: event => this.stopRegionGroupEvent(event),
        click: event => this.toggleRegionGroup(item.region, event),
        keydown: event => this.onRegionGroupKeydown(item.region, event, on && on.keydown),
      }
    },
    onInput(value) {
      if (this.isSearchHeaderValue(value)) {
        this.openMenu()
        return
      }

      const selectedItem = this.allRegionItemsByValue[value]
      if (selectedItem && selectedItem.type === 'region' && selectedItem.hasBranches) {
        this.expandedRegion = selectedItem.region
        this.collapsedRegion = ''
        this.openMenu()
        return
      }

      this.search = ''
      this.expandedRegion = ''
      this.collapsedRegion = ''
      this.$emit('input', value)
      this.$emit('change', value)
    },
    isSearchHeaderValue(value) {
      return typeof value === 'string' && value.startsWith('search-header:')
    },
    onRegionGroupKeydown(region, event, originalHandler) {
      if (event && (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar')) {
        this.toggleRegionGroup(region, event)
        return
      }

      if (typeof originalHandler === 'function') {
        originalHandler(event)
      }
    },
    toggleRegionGroup(region, event) {
      this.stopRegionGroupEvent(event)

      this.search = ''

      if (this.currentExpandedRegion === region) {
        this.expandedRegion = ''
        this.collapsedRegion = region
      } else {
        this.expandedRegion = region
        this.collapsedRegion = ''
      }

      this.openMenu()
    },
    stopRegionGroupEvent(event) {
      if (!event) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    },
    openMenu() {
      this.$nextTick(() => {
        if (this.$refs.regionAutocomplete) {
          this.$refs.regionAutocomplete.isMenuActive = true
        }
      })
    },
  },
}
</script>

<style scoped>
.region-group-option {
  margin: 0;
  border-radius: 0;
  background: #fff;
}

.region-group-option--expanded {
  margin: 6px 8px 2px;
  border-radius: 10px;
  background: #fbf6d7;
}

.region-group-option--expanded .v-list-item__title {
  color: #9d8700;
  font-weight: 600;
}

.region-group-option--expanded .v-list-item__subtitle {
  color: #8b8b8b;
  margin-top: 2px;
}

.region-office-item {
  margin: 0 8px 4px 28px;
  border-left: 2px solid #e7d86c;
}

.region-office-option {
  padding: 4px 0 4px 14px;
}

.region-search-header {
  color: #9d8700;
  font-size: 0.875rem;
  font-weight: 600;
  margin: 6px 8px 0 28px;
  min-height: auto;
  padding: 0 0 4px 14px;
}
</style>
